import { HubPersona, LinkedContact, WellKnownFolderNames, Person, PersonType } from '../../shared';

export interface ContactStatistics {
    contactSourceCounts: { [source: string]: number };
    contactsPerPersonaCounts: { [combination: string]: number };
    jsMvvMFavoritesCount: number;
    rogueRecipientCacheContactCounts: { [type: string]: number };
}

/**
 * Returns the contact source name. Only return sourceFolderName if it is WindowsLive Contacts, as any
 * other value would be PII. WindowsLive Contacts have sourceDisplayName set to "Outlook" just like normal contacts, but
 * the folder is read-only and cause unlinking to fail. Hence we need to return sourceFolderName to identify it.
 * @param contact The contact
 */
export function getContactSourceName(contact: LinkedContact): string {
    return contact.sourceFolderName === WellKnownFolderNames.WindowsLiveContacts
        ? contact.sourceFolderName
        : contact.sourceDisplayName;
}

/**
 * Returns an array of source names for all contacts in a persona.
 * @param persona The persona
 */
export function getPersonaContactSourceNames(persona: HubPersona): string[] {
    if (persona.type === PersonType.PrivateDistributionList) {
        return ['PDL'];
    }

    if (persona.type === PersonType.FavoritePerson) {
        return ['Favorites'];
    }

    if (persona.type === PersonType.FavoritePrivateDistributionList) {
        return ['FavoritePrivateDistributionList'];
    }

    if (persona.type === PersonType.FavoriteUnifiedGroup) {
        return ['FavoriteUnifiedGroup'];
    }

    if (persona.type === PersonType.PublicDistributionList) {
        return ['PublicDistributionList'];
    }

    if (persona.type === PersonType.Group || persona.type === PersonType.GroupBasicData) {
        return ['Group'];
    }

    if (persona.type === PersonType.Generic) {
        return ['Generic'];
    }

    if (!persona.linkedContacts || persona.linkedContacts.length === 0) {
        return ['Unknown'];
    }

    const names: string[] = [];
    persona.linkedContacts.forEach(contact => names.push(getContactSourceName(contact)));

    // Legacy FindPeople doesn't return GAL sources in AttributionsArray, but if the persona is linked to
    // a GAL source, the GALContactGuid is set, so we use it to determine if there is a linked GAL source.
    if (persona.info?.GALContactGuid) {
        names.push('GAL');
    }

    return names;
}

/**
 * Update statistics about contacts given the new batch of contacts and the contact statistics.
 * Includes contact source counts, and count of OWS favorites
 * @param personas The new batch of personas
 * @param contactStatistics The statistics to be updated
 */
export function updateContactStatistics(
    personas: HubPersona[],
    contactStatistics: ContactStatistics
): void {
    const contactSourceCounts = getPersonasContactSourceCounts(
        personas,
        contactStatistics.contactSourceCounts
    );

    const contactsPerPersonaCounts = getContactsPerPersonaCounts(
        personas,
        contactStatistics.contactsPerPersonaCounts
    );

    const owsFavoritesCount = getjsMVVMFavoritesCount(
        personas,
        contactStatistics.jsMvvMFavoritesCount
    );

    const rogueRecipientCacheContactCounts = getRogueRecipientCacheContactCounts(
        personas,
        contactStatistics.rogueRecipientCacheContactCounts
    );

    contactStatistics.contactSourceCounts = contactSourceCounts;
    contactStatistics.contactsPerPersonaCounts = contactsPerPersonaCounts;
    contactStatistics.jsMvvMFavoritesCount = owsFavoritesCount;
    contactStatistics.rogueRecipientCacheContactCounts = rogueRecipientCacheContactCounts;
}

/**
 * Return the new count of favorites from the JSMvvM people hub, mastered on the contact objects,
 * given the new personas batch and the current count
 * @param personas The new batch of personas
 * @param currentjsMVVMFavoritesCount Current count of OWS favorites
 */
function getjsMVVMFavoritesCount(
    personas: HubPersona[],
    currentjsMVVMFavoritesCount: number
): number {
    const currentBatchCount = personas
        .filter(persona => persona.type === PersonType.Person)
        .filter((person: Person) => person.isjsMVVMFavorite).length;
    return currentBatchCount + currentjsMVVMFavoritesCount;
}

/**
 * Returns the counts of the different contact sources in an array of personas. If previous
 * counts are provided, then the new counts are added to those.
 * @param personas The array of personas
 * @param counts Previous counts to add to
 */
function getPersonasContactSourceCounts(
    personas: HubPersona[],
    counts: { [source: string]: number } = {}
): { [source: string]: number } {
    personas.forEach(persona => {
        getPersonaContactSourceNames(persona).forEach(source => {
            counts[source] = (counts[source] || 0) + 1;
        });
    });

    return counts;
}

/**
 * Returns the count of the number of contacts of various combinations for each persona
 * @param personas The array of personas
 * @param counts Previous counts to add to
 */
function getContactsPerPersonaCounts(
    personas: HubPersona[],
    counts: { [combination: string]: number } = {}
): { [combination: string]: number } {
    let contactToContactLink = 0; // Number of personas where two or more explicit contacts are linked
    let contactToGalLink = 0; // Number of personas where one or more explicit contacts are linked to a GAL contact
    let extraContacts = 0; // Number of extra contacts that will be shown if linked contacts are not to be aggregated as personas

    personas.forEach(persona => {
        const keyGal = 'GAL';
        const keyOutlook = 'Outlook';
        const contactSourceCounts = getPersonasContactSourceCounts([persona], {});
        const keys = Object.keys(contactSourceCounts);

        let hasGal = false;
        let hasOutlook = false;

        keys &&
            keys.forEach(key => {
                if (key === keyGal) {
                    hasGal = true;
                }

                if (key === keyOutlook) {
                    hasOutlook = true;
                }
            });

        const outlookCount = hasOutlook ? contactSourceCounts[keyOutlook] : 0;

        if (hasGal && hasOutlook) {
            contactToGalLink++;
        }

        if (!hasGal && outlookCount > 1) {
            contactToContactLink++;
        }

        if (outlookCount > 1) {
            extraContacts += outlookCount - 1;
        }

        const combination = JSON.stringify(contactSourceCounts);
        counts[combination] = (counts[combination] || 0) + 1;
    });

    counts['ContactToContactLink'] = (counts['ContactToContactLink'] || 0) + contactToContactLink;
    counts['ContactToGalLink'] = (counts['ContactToGalLink'] || 0) + contactToGalLink;
    counts['ExtraContacts'] = (counts['ExtraContacts'] || 0) + extraContacts;
    counts['PersonaCount'] = (counts['PersonaCount'] || 0) + personas.length;

    return counts;
}

/**
 * Returns the counts of how many personas have at least one rogue Recipient Cache contact ("Yes"), and how many have none ("No")
 * @param personas The array of personas
 * @param counts Previous counts to add to
 */
function getRogueRecipientCacheContactCounts(
    personas: HubPersona[],
    counts: { [type: string]: number } = {}
): { [type: string]: number } {
    personas.forEach(person => {
        if (person.type === PersonType.Person) {
            const type = person.hasRogueRecipientCacheContact ? 'Yes' : 'No';
            counts[type] = (counts[type] || 0) + 1;
        }
    });

    return counts;
}
